import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  CreateServiceLevelRequest,
  RoutingConfigSummaryRecord,
  ServiceLevelRecord,
  UpdateServiceLevelRequest,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCreateServiceLevel,
  useServiceLevels,
  useUpdateServiceLevel,
} from '../api/useServiceLevels.ts';
import { useRoutingConfigs } from '../api/useTenantRouting.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { isDeprecated } from './tenantRoutingShared.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
  TBody,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';
import type { SelectOption } from '../ui/index.ts';

/**
 * Service level catalogue (#1723/#1725) — admin only.
 *
 * A level is the customer-facing name for a routing configuration: the
 * customer manager picks a level by name on Customer 360 and never learns
 * which config it anchors. Naming that config is what makes THIS page
 * admin-only, and the role set is the same constant the nav item, the route
 * guard, and the permission matrix carry.
 *
 * Two field-level traps the gateway validator sets, both handled by trimming
 * before submit (`service-levels-page.test.tsx` pins them):
 *   - on a nullable field (`description`, `routing_config_id`) `""` clears the
 *     value but `"   "` is a 400 — so an emptied box is sent as `null`;
 *   - `name` is non-nullable, so an empty one never leaves the browser.
 */

/**
 * Radix Select rejects an empty option value, so "no anchored config" needs a
 * sentinel. It is mapped back to `null` at the wire boundary.
 */
const NO_ANCHOR = 'none';

interface LevelFormState {
  readonly name: string;
  readonly description: string;
  readonly anchor: string;
}

interface LevelFormErrors {
  name?: string;
  anchor?: string;
}

const EMPTY_LEVEL_FORM: LevelFormState = { name: '', description: '', anchor: NO_ANCHOR };

function nullableText(value: string): string | null {
  const normalized = value.trim();
  return normalized.length === 0 ? null : normalized;
}

function anchorToWire(anchor: string): string | null {
  return anchor === NO_ANCHOR ? null : anchor;
}

function focusField(fieldId: string): void {
  queueMicrotask(() => document.getElementById(fieldId)?.focus());
}

function anchorOptions(
  t: ReturnType<typeof useT>,
  configs: readonly RoutingConfigSummaryRecord[],
  selected: string,
): readonly SelectOption[] {
  // #1919. Deprecated configs drop out of the picker: anchoring a level is
  // authoring a new binding path, and the gateway refuses it. Filtering here
  // only stops the operator from being offered a choice that would 409.
  const selectable = configs.filter(
    (config) => config.status === 'active' && !isDeprecated(config),
  );
  const options: SelectOption[] = [{ value: NO_ANCHOR, label: t('service_levels.anchor.none') }];
  for (const config of selectable) {
    options.push({ value: config.id, label: config.name });
  }
  // A level already anchored to a config that has since been archived OR
  // deprecated must still render its own value — otherwise the trigger goes
  // blank and saving the form would silently unbind the anchor.
  if (selected !== NO_ANCHOR && !selectable.some((config) => config.id === selected)) {
    const current = configs.find((config) => config.id === selected) ?? null;
    options.push({
      value: selected,
      // A deprecated anchor is still serving its members, so it is labelled as
      // deprecated rather than "unavailable" — the two states differ in what
      // the operator should do next.
      label: current !== null && isDeprecated(current)
        ? t('service_levels.anchor.deprecated', { name: current.name })
        : t('service_levels.anchor.unavailable', { id: selected }),
    });
  }
  return options;
}

function mutationFieldError(
  t: ReturnType<typeof useT>,
  cause: unknown,
): LevelFormErrors | null {
  if (!(cause instanceof OpsApiError)) return null;
  if (cause.code === 'SERVICE_LEVEL_NAME_TAKEN') {
    return { name: t('service_levels.validation.name_taken') };
  }
  if (cause.code === 'ROUTE_BINDING_NOT_FOUND') {
    return { anchor: t('service_levels.validation.anchor_missing') };
  }
  // #1919. Reachable despite the picker filtering deprecated configs: the list
  // can be stale by the time the form is submitted, and the gateway is the
  // authority. Naming the field beats a raw English toast over the anchor.
  if (cause.code === 'ROUTE_CONFIG_DEPRECATED') {
    return { anchor: t('service_levels.validation.anchor_deprecated') };
  }
  return null;
}

export function ServiceLevelsPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { data: levels, isLoading, error, refetch, isFetching } = useServiceLevels();
  const configs = useRoutingConfigs();
  const createMut = useCreateServiceLevel();
  const updateMut = useUpdateServiceLevel();

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<LevelFormState>(EMPTY_LEVEL_FORM);
  const [createErrors, setCreateErrors] = useState<LevelFormErrors>({});
  const [manageTarget, setManageTarget] = useState<ServiceLevelRecord | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<ServiceLevelRecord | null>(null);

  const configList = configs.data?.configs ?? [];
  const configNames = useMemo(() => {
    const byId = new Map<string, string>();
    for (const config of configList) byId.set(config.id, config.name);
    return byId;
  }, [configList]);

  function closeCreate(): void {
    setCreateOpen(false);
    setCreateForm(EMPTY_LEVEL_FORM);
    setCreateErrors({});
    createMut.reset();
  }

  function handleCreate(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    // The submit button is disabled while in flight, but Enter in a text input
    // submits the form regardless — without this a second POST lands and its
    // SERVICE_LEVEL_NAME_TAKEN paints a field error over a level that was in
    // fact created.
    if (createMut.isPending) return;
    const name = createForm.name.trim();
    if (name.length === 0) {
      setCreateErrors({ name: t('service_levels.validation.name_required') });
      focusField('service-levels-create-name');
      return;
    }
    setCreateErrors({});
    const body: CreateServiceLevelRequest = {
      name,
      description: nullableText(createForm.description),
      routing_config_id: anchorToWire(createForm.anchor),
    };
    createMut.mutate(body, {
      onSuccess: () => {
        toast.success(t('service_levels.toast.created'));
        closeCreate();
      },
      onError: (cause) => {
        const fieldError = mutationFieldError(t, cause);
        if (fieldError !== null) {
          setCreateErrors(fieldError);
          focusField(fieldError.name !== undefined
            ? 'service-levels-create-name'
            : 'service-levels-create-anchor');
        }
        toast.error(coerceErrorMessage(cause));
      },
    });
  }

  function setStatus(level: ServiceLevelRecord, status: 'active' | 'archived'): void {
    updateMut.mutate({ levelId: level.id, body: { status } }, {
      onSuccess: () => {
        toast.success(status === 'archived'
          ? t('service_levels.toast.archived')
          : t('service_levels.toast.restored'));
        setArchiveTarget(null);
      },
      onError: (cause) => { toast.error(coerceErrorMessage(cause)); },
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('service_levels.title')}
        sub={t('service_levels.subtitle')}
        actions={
          <Button
            variant="primary"
            onClick={() => setCreateOpen(true)}
            data-testid="service-levels-create-btn"
            disabled={isLoading || error !== null}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            {t('service_levels.create_btn')}
          </Button>
        }
      />

      {isLoading ? (
        <div
          data-testid="service-levels-loading"
          className="flex flex-col gap-4"
          aria-label={t('service_levels.loading')}
        >
          {[1, 2, 3].map((item) => (
            <Skeleton key={item} className="h-12 w-full" />
          ))}
        </div>
      ) : error !== null ? (
        <div
          data-testid="service-levels-error"
          className="flex flex-col items-start gap-3 rounded-lg border border-bad-line bg-bad-dim px-4 py-3"
          role="alert"
        >
          <div className="flex flex-col gap-1">
            <p className="m-0 text-body font-medium text-bad-bright">{t('service_levels.error.title')}</p>
            <p className="m-0 text-dense text-bad-bright">{t('service_levels.error.hint')}</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { void refetch(); }}
            loading={isFetching}
            data-testid="service-levels-retry"
          >
            {isFetching ? t('service_levels.error.retrying') : t('service_levels.error.retry')}
          </Button>
        </div>
      ) : levels === undefined || levels.length === 0 ? (
        <EmptyState
          testId="service-levels-empty"
          title={t('service_levels.empty.title')}
          hint={t('service_levels.empty.hint')}
          action={(
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" className="h-4 w-4" />
              {t('service_levels.empty.action')}
            </Button>
          )}
        />
      ) : (
        <Table testId="service-levels-table" className="[&>table]:min-w-[880px]">
          <THead>
            <Tr interactive={false}>
              <Th>{t('service_levels.col.name')}</Th>
              <Th>{t('service_levels.col.description')}</Th>
              <Th>{t('service_levels.col.anchor')}</Th>
              <Th>{t('service_levels.col.customers')}</Th>
              <Th>{t('service_levels.col.status')}</Th>
              <Th className="text-right">{t('service_levels.col.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {levels.map((level) => (
              <ServiceLevelRow
                key={level.id}
                level={level}
                anchorName={level.routing_config_id === null
                  ? null
                  : configNames.get(level.routing_config_id) ?? null}
                pending={updateMut.isPending}
                onManage={setManageTarget}
                onArchive={setArchiveTarget}
                onRestore={(target) => setStatus(target, 'active')}
              />
            ))}
          </TBody>
        </Table>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!open && !createMut.isPending) closeCreate();
        }}
        title={t('service_levels.create_modal.title')}
        description={t('service_levels.create_modal.description')}
        closeLabel={t('common.close')}
        testId="service-levels-create-dialog"
      >
        <form onSubmit={handleCreate} noValidate className="flex flex-col gap-4">
          <FormField
            label={t('service_levels.field.name')}
            fieldId="service-levels-create-name"
            required
            error={createErrors.name}
          >
            <Input
              id="service-levels-create-name"
              value={createForm.name}
              onChange={(event) => {
                const next = event.target.value;
                setCreateForm((current) => ({ ...current, name: next }));
                setCreateErrors((current) => ({ ...current, name: undefined }));
              }}
              placeholder={t('service_levels.field.name_placeholder')}
              data-testid="service-levels-create-name"
              disabled={createMut.isPending}
              required
              invalid={createErrors.name !== undefined}
              aria-describedby={createErrors.name !== undefined ? 'service-levels-create-name-error' : undefined}
            />
          </FormField>
          <FormField
            label={t('service_levels.field.description')}
            fieldId="service-levels-create-description"
            hint={t('service_levels.field.description_hint')}
          >
            <Textarea
              id="service-levels-create-description"
              value={createForm.description}
              onChange={(event) => {
                const next = event.target.value;
                setCreateForm((current) => ({ ...current, description: next }));
              }}
              placeholder={t('service_levels.field.description_placeholder')}
              data-testid="service-levels-create-description"
              disabled={createMut.isPending}
              rows={3}
            />
          </FormField>
          <AnchorField
            fieldId="service-levels-create-anchor"
            value={createForm.anchor}
            configs={configList}
            configsLoading={configs.isLoading}
            configsError={configs.isError}
            error={createErrors.anchor}
            disabled={createMut.isPending}
            onChange={(value) => {
              setCreateForm((current) => ({ ...current, anchor: value }));
              setCreateErrors((current) => ({ ...current, anchor: undefined }));
            }}
          />

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={closeCreate} disabled={createMut.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createMut.isPending}
              data-testid="service-levels-create-submit"
            >
              {t('service_levels.create_modal.submit')}
            </Button>
          </div>
        </form>
      </Dialog>

      {manageTarget !== null ? (
        <ManageServiceLevelDialog
          level={manageTarget}
          configs={configList}
          configsLoading={configs.isLoading}
          configsError={configs.isError}
          onClose={() => setManageTarget(null)}
        />
      ) : null}

      <ConfirmDialog
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open && !updateMut.isPending) setArchiveTarget(null);
        }}
        title={t('service_levels.archive_modal.title')}
        body={t('service_levels.archive_modal.body', { name: archiveTarget?.name ?? '' })}
        confirmLabel={t('service_levels.archive_modal.confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={() => {
          if (archiveTarget !== null) setStatus(archiveTarget, 'archived');
        }}
        tone="danger"
        loading={updateMut.isPending}
        testId="service-levels-archive-confirm"
      />
    </div>
  );
}

function AnchorField({
  fieldId,
  value,
  configs,
  configsLoading,
  configsError,
  error,
  disabled,
  onChange,
}: {
  readonly fieldId: string;
  readonly value: string;
  readonly configs: readonly RoutingConfigSummaryRecord[];
  readonly configsLoading: boolean;
  readonly configsError: boolean;
  readonly error: string | undefined;
  readonly disabled: boolean;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <FormField
      label={t('service_levels.field.anchor')}
      fieldId={fieldId}
      hint={configsError
        ? t('service_levels.field.anchor_error')
        : t('service_levels.field.anchor_hint')}
      error={error}
    >
      <Select
        id={fieldId}
        value={value}
        onValueChange={onChange}
        options={anchorOptions(t, configs, value)}
        ariaLabel={t('service_levels.field.anchor')}
        testId={fieldId}
        disabled={disabled || configsLoading || configsError}
        invalid={error !== undefined}
        ariaDescribedBy={error !== undefined ? `${fieldId}-error` : undefined}
      />
    </FormField>
  );
}

function ServiceLevelRow({
  level,
  anchorName,
  pending,
  onManage,
  onArchive,
  onRestore,
}: {
  readonly level: ServiceLevelRecord;
  readonly anchorName: string | null;
  readonly pending: boolean;
  readonly onManage: (level: ServiceLevelRecord) => void;
  readonly onArchive: (level: ServiceLevelRecord) => void;
  readonly onRestore: (level: ServiceLevelRecord) => void;
}): JSX.Element {
  const t = useT();
  const archived = level.status === 'archived';
  return (
    <Tr data-testid={`service-level-row-${level.id}`}>
      <Td emphasis>{level.name}</Td>
      <Td>
        {level.description === null ? (
          <span className="text-paper-low">{t('service_levels.description.empty')}</span>
        ) : (
          level.description
        )}
      </Td>
      <Td>
        {level.routing_config_id === null ? (
          <span className="text-paper-low">{t('service_levels.anchor.none')}</span>
        ) : (
          <span className="font-mono text-micro text-paper-mid">
            {anchorName ?? level.routing_config_id}
          </span>
        )}
      </Td>
      <Td>{level.assigned_org_count}</Td>
      <Td>
        <Chip tone={archived ? 'neutral' : 'ok'}>
          {archived ? t('service_levels.status.archived') : t('service_levels.status.active')}
        </Chip>
      </Td>
      <Td className="whitespace-nowrap text-right">
        <div className="flex flex-nowrap items-center justify-end gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onManage(level)}
            data-testid={`service-level-row-${level.id}-manage`}
          >
            {t('service_levels.row.manage')}
          </Button>
          {archived ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={pending}
              onClick={() => onRestore(level)}
              data-testid={`service-level-row-${level.id}-restore`}
            >
              {t('service_levels.row.restore')}
            </Button>
          ) : (
            <Button
              variant="danger"
              size="sm"
              disabled={pending}
              onClick={() => onArchive(level)}
              data-testid={`service-level-row-${level.id}-archive`}
            >
              {t('service_levels.row.archive')}
            </Button>
          )}
        </div>
      </Td>
    </Tr>
  );
}

function ManageServiceLevelDialog({
  level,
  configs,
  configsLoading,
  configsError,
  onClose,
}: {
  readonly level: ServiceLevelRecord;
  readonly configs: readonly RoutingConfigSummaryRecord[];
  readonly configsLoading: boolean;
  readonly configsError: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const updateMut = useUpdateServiceLevel();
  const [form, setForm] = useState<LevelFormState>({
    name: level.name,
    description: level.description ?? '',
    anchor: level.routing_config_id ?? NO_ANCHOR,
  });
  const [errors, setErrors] = useState<LevelFormErrors>({});

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (updateMut.isPending) return;
    const name = form.name.trim();
    if (name.length === 0) {
      setErrors({ name: t('service_levels.validation.name_required') });
      focusField('service-levels-manage-name');
      return;
    }
    setErrors({});
    const body: UpdateServiceLevelRequest = {
      name,
      description: nullableText(form.description),
      routing_config_id: anchorToWire(form.anchor),
    };
    updateMut.mutate({ levelId: level.id, body }, {
      onSuccess: () => {
        toast.success(t('service_levels.toast.updated'));
        onClose();
      },
      onError: (cause) => {
        const fieldError = mutationFieldError(t, cause);
        if (fieldError !== null) {
          setErrors(fieldError);
          focusField(fieldError.name !== undefined
            ? 'service-levels-manage-name'
            : 'service-levels-manage-anchor');
        }
        toast.error(coerceErrorMessage(cause));
      },
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !updateMut.isPending) onClose();
      }}
      title={t('service_levels.manage_modal.title', { name: level.name })}
      description={t('service_levels.manage_modal.description')}
      closeLabel={t('common.close')}
      testId="service-levels-manage-dialog"
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <FormField
          label={t('service_levels.field.name')}
          fieldId="service-levels-manage-name"
          required
          error={errors.name}
        >
          <Input
            id="service-levels-manage-name"
            value={form.name}
            onChange={(event) => {
              const next = event.target.value;
              setForm((current) => ({ ...current, name: next }));
              setErrors((current) => ({ ...current, name: undefined }));
            }}
            data-testid="service-levels-manage-name"
            disabled={updateMut.isPending}
            required
            invalid={errors.name !== undefined}
            aria-describedby={errors.name !== undefined ? 'service-levels-manage-name-error' : undefined}
          />
        </FormField>
        <FormField
          label={t('service_levels.field.description')}
          fieldId="service-levels-manage-description"
          hint={t('service_levels.field.description_hint')}
        >
          <Textarea
            id="service-levels-manage-description"
            value={form.description}
            onChange={(event) => {
              const next = event.target.value;
              setForm((current) => ({ ...current, description: next }));
            }}
            data-testid="service-levels-manage-description"
            disabled={updateMut.isPending}
            rows={3}
          />
        </FormField>
        <AnchorField
          fieldId="service-levels-manage-anchor"
          value={form.anchor}
          configs={configs}
          configsLoading={configsLoading}
          configsError={configsError}
          error={errors.anchor}
          disabled={updateMut.isPending}
          onChange={(value) => {
            setForm((current) => ({ ...current, anchor: value }));
            setErrors((current) => ({ ...current, anchor: undefined }));
          }}
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={updateMut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={updateMut.isPending}
            data-testid="service-levels-manage-submit"
          >
            {t('service_levels.manage_modal.submit')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
